import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/notify';
import { format } from 'date-fns';
import {
  getOutboxScheduleBanner,
  getOutboxStatusBadge,
  getOutboxStatusSubtext,
  isAdHocScheduledOutboxDraft,
  resolveOutboxDisplayStatus,
} from '@/lib/outboxStatusDisplay';
import { PersonFinal } from '@/api/entities';
import { useUser } from '@/contexts/UserContext';
import { evidenceFromGenerationPayload, evidenceHasContent, formatEvidenceScore } from '@/lib/outboxEvidence';
import { diffWords } from 'diff';
import { 
  X, 
  Send, 
  Calendar,
  Clock,
  User as UserIcon,
  Building2,
  Sparkles,
  TrendingUp,
  Award,
  FileText,
  Copy,
  CheckCircle2,
  Loader2,
  Mail,
  Linkedin,
  Phone,
  Wand2,
  ArrowDownToLine,
  ArrowUpFromLine,
  RotateCcw,
  Check,
  AlertTriangle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Globe,
  Link2,
  Info,
} from 'lucide-react';
import { isOutlookBackfilledMessage } from '@/lib/outlookBackfillMessage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  logAccountInteraction,
  extractAccountId,
  extractPersonId,
  extractStepId
} from '@/components/utils/analytics';
import { integratePersonaAsset, normalizeIntegrateAssetChannel } from '@/lib/integratePersonaAsset';
import LinkedInSubtypeBadge from '@/components/common/LinkedInSubtypeBadge';
import { getConnectedEmailProviderTokens as resolveEmailProviderTokens } from '@/lib/emailProviderTokens';
import {
  buildOutboxChannelRecordingPayload,
  countPriorExecutedLinkedInViaPreviousTouch,
  enrichDraftLinkedInContext,
  normalizeOutboxDraftRow,
  fetchSequenceTouchPlan,
  getChannelDisplayLabel,
  getPlaybookIdForOutboxDraft,
  getSequencePlanFromCache,
  resolveDiyLinkedInChannelForDraft,
  getOutboxStageNo,
  isLinkedInConnectionRequest,
  isLinkedInOutboxChannel,
  LINKEDIN_CHANNEL_DEBUG_BUILD,
  logLinkedInChannelResolution,
} from '@/lib/outboxChannel';
import { isoToDatetimeLocalValue } from '@/lib/outboxRecommendedSendTime';
import RecommendedSendTimeCard from '@/components/outbox/RecommendedSendTimeCard';
import { outboxBodyHtmlNeedsFetch, resolveOutboxBodyHtml } from '@/lib/outboxBodyHtml';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';
const ARABIC_TEXT_REGEX = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
const URL_TEXT_REGEX = /\bhttps?:\/\/[^\s<>"')]+/gi;
const MIN_ARABIC_CHARS_FOR_RTL = 2;
function orderedPersonKeysForAssets(draft) {
  const keys = [
    draft?.person_id,
    draft?.persona_id,
    draft?.personId,
    draft?.person?.person_id,
    draft?.person?.persona_id,
    draft?.person?.id,
  ]
    .filter(Boolean)
    .map((k) => String(k).trim())
    .filter(Boolean);
  return [...new Set(keys)];
}

/** Ad-hoc generation metadata only — omit for playbook/sequence drafts (no used_signals / focus on draft). */
function pickUsedSignalsAndFocus(draft) {
  const extra = {};
  if (Array.isArray(draft?.used_signals)) extra.used_signals = draft.used_signals;
  if (typeof draft?.focus === 'string') extra.focus = draft.focus;
  if (draft?.context_signals && typeof draft.context_signals === 'object') {
    extra.context_signals = draft.context_signals;
  }
  if (draft?.style_sample_id != null && draft.style_sample_id !== '') {
    extra.style_sample_id = draft.style_sample_id;
  }
  return extra;
}

const LANGUAGE_OPTIONS = [
  { value: 'Arabic', label: 'Arabic', flag: '🇸🇦' },
  { value: 'Danish', label: 'Danish', flag: '🇩🇰' },
  { value: 'Dutch', label: 'Dutch', flag: '🇳🇱' },
  { value: 'English', label: 'English', flag: '🇬🇧' },
  { value: 'English (US)', label: 'English (US)', flag: '🇺🇸' },
  { value: 'Finnish', label: 'Finnish', flag: '🇫🇮' },
  { value: 'French', label: 'French', flag: '🇫🇷' },
  { value: 'German', label: 'German', flag: '🇩🇪' },
  { value: 'Hebrew', label: 'Hebrew', flag: '🇮🇱' },
  { value: 'Hindi', label: 'Hindi', flag: '🇮🇳' },
  { value: 'Italian', label: 'Italian', flag: '🇮🇹' },
  { value: 'Japanese', label: 'Japanese', flag: '🇯🇵' },
  { value: 'Korean', label: 'Korean', flag: '🇰🇷' },
  { value: 'Mandarin Chinese', label: 'Mandarin Chinese', flag: '🇨🇳' },
  { value: 'Norwegian', label: 'Norwegian', flag: '🇳🇴' },
  { value: 'Polish', label: 'Polish', flag: '🇵🇱' },
  { value: 'Portuguese', label: 'Portuguese', flag: '🇵🇹' },
  { value: 'Portuguese (Brazilian)', label: 'Portuguese (Brazilian)', flag: '🇧🇷' },
  { value: 'Spanish', label: 'Spanish', flag: '🇪🇸' },
  { value: 'Swedish', label: 'Swedish', flag: '🇸🇪' },
  { value: 'Turkish', label: 'Turkish', flag: '🇹🇷' },
];

  export default function OutboxDrawer({ draft, onClose, onSent, tenantId, userId, peerDrafts = [] }) {
    const contextUser = useUser();
    const [fullPerson, setFullPerson] = useState(null);
    const [sequenceTouches, setSequenceTouches] = useState(null);
    const [planTouches, setPlanTouches] = useState(null);
    const [diyChannelResolved, setDiyChannelResolved] = useState(null);
    const [priorLinkedInFromWalk, setPriorLinkedInFromWalk] = useState(0);

    useEffect(() => {
      setSequenceTouches(null);
      setPlanTouches(null);
      setDiyChannelResolved(null);
      setPriorLinkedInFromWalk(0);
      const seqId = draft?.sequence_id || draft?.sequenceId || draft?.sequence?.id;
      const personId =
        draft?.person_id ||
        draft?.personId ||
        draft?.person?.person_id ||
        draft?.person?.id;
      const uid = userId || contextUser?.id || contextUser?.user_id;
      if (!seqId || !personId || !tenantId || !uid || !isLinkedInOutboxChannel(draft)) return;

      let cancelled = false;
      (async () => {
        const [touches, walkPrior, diyChannel] = await Promise.all([
          fetchSequenceTouchPlan(seqId, personId, {
            tenantId,
            userId: uid,
            apiBase: API_BASE_URL,
          }),
          countPriorExecutedLinkedInViaPreviousTouch(seqId, getOutboxStageNo(draft), {
            tenantId,
            userId: uid,
            apiBase: API_BASE_URL,
          }),
          resolveDiyLinkedInChannelForDraft(draft, {
            tenantId,
            userId: uid,
            apiBase: API_BASE_URL,
          }),
        ]);
        if (!cancelled) {
          setSequenceTouches(touches);
          setPriorLinkedInFromWalk(walkPrior);
          setDiyChannelResolved(diyChannel);
          setPlanTouches(getSequencePlanFromCache(seqId, personId).plan);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [
      draft?.sequence_id,
      draft?.sequenceId,
      draft?.sequence?.id,
      draft?.person_id,
      draft?.personId,
      draft?.person?.person_id,
      draft?.person?.id,
      draft?.channel,
      draft?.stage,
      draft?.stage_no,
      draft?.playbook_id,
      draft?.playbook_key,
      draft?.sequence?.playbook_id,
      tenantId,
      userId,
      contextUser?.id,
      contextUser?.user_id,
    ]);

    const channelDraft = useMemo(() => {
      const stripped = normalizeOutboxDraftRow({
        ...draft,
        person: fullPerson || draft.person,
        linkedin_connected:
          fullPerson?.linkedin_connected ??
          draft.person?.linkedin_connected ??
          draft.linkedin_connected,
      });
      delete stripped.__linkedinTouchOrder;
      delete stripped.__linkedinPriorExecuted;
      delete stripped.__diyLinkedInChannel;
      delete stripped.__diyPlaybookLinkedInChannel;
      delete stripped.__linkedinTemplateChannelRaw;
      return enrichDraftLinkedInContext(stripped, peerDrafts, sequenceTouches, {
        extraPriorExecuted: priorLinkedInFromWalk,
        planTouches,
        diyChannelResolved,
      });
    }, [
      draft,
      peerDrafts,
      sequenceTouches,
      planTouches,
      diyChannelResolved,
      priorLinkedInFromWalk,
      fullPerson,
    ]);
    const draftId = channelDraft?.stepId || channelDraft?.id;
    const isTemporary = channelDraft?.is_temporary || draftId?.startsWith('draft-');
    const stepStatus = resolveOutboxDisplayStatus(channelDraft);
    const statusBadge = getOutboxStatusBadge(stepStatus);
    const statusSubtext = getOutboxStatusSubtext(stepStatus, channelDraft);
    const scheduleBanner = getOutboxScheduleBanner(channelDraft);
    const isSent = stepStatus === 'sent';
    const isScheduled = stepStatus === 'scheduled';
    const isHistoryItem = channelDraft?.is_history || channelDraft?.status === 'manual_completion';
    const isAdHocScheduledDraft = isAdHocScheduledOutboxDraft(channelDraft);
    const isOutlookBackfill = isOutlookBackfilledMessage(channelDraft);
    
    console.log('🔍 OutboxDrawer:', { 
      tenantId, 
      userId, 
      draftId,
      linkedInDebugBuild: LINKEDIN_CHANNEL_DEBUG_BUILD,
      stepId: channelDraft?.stepId,
      id: channelDraft?.id,
      isTemporary,
      hasBodyHtml: !!(channelDraft?.body_html || channelDraft?.bodyHtml || channelDraft?.body || channelDraft?.html),
      hasSequenceId: !!(channelDraft?.sequence_id || channelDraft?.sequenceId),
      hasPerson: !!channelDraft?.person,
      hasPersonId: !!(channelDraft?.person_id || channelDraft?.personId),
      channel: channelDraft?.channel,
      stage: channelDraft?.stage || channelDraft?.stage_no,
      linkedinTouchOrder: channelDraft?.__linkedinTouchOrder,
      stageNo: getOutboxStageNo(channelDraft),
      sequenceTouchPlanCount: Array.isArray(sequenceTouches) ? sequenceTouches.length : 0,
      playbookId: getPlaybookIdForOutboxDraft(channelDraft) || null,
      priorLinkedInFromWalk,
      peerDraftCount: peerDrafts?.length ?? 0,
    });
    logLinkedInChannelResolution('OutboxDrawer:open', channelDraft);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const bodyRef = useRef('');
  useEffect(() => {
    bodyRef.current = body;
  }, [body]);
  const [evidence, setEvidence] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [preferredAlias, setPreferredAlias] = useState(null);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [customScheduleTime, setCustomScheduleTime] = useState('');

  const scheduleUserId = userId || contextUser?.user_id || contextUser?.id;

  // AI Edit state
  const [showAIEdit, setShowAIEdit] = useState(false);
  const [aiEditAction, setAiEditAction] = useState('shorten');
  const [aiEditTone, setAiEditTone] = useState('');
  const [aiEditLanguage, setAiEditLanguage] = useState('');
  const [aiEditInstruction, setAiEditInstruction] = useState('');
  const [aiVariants, setAiVariants] = useState([]);
  const [isGeneratingVariants, setIsGeneratingVariants] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [originalBody, setOriginalBody] = useState('');
  const [undoHistory, setUndoHistory] = useState([]);
  const [previousTouch, setPreviousTouch] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  /** Per-persona cache for ad-hoc available assets (lifetime of page / drawer module). */
  const availableAssetsCacheRef = useRef(new Map());
  const [adHocAvailableAssets, setAdHocAvailableAssets] = useState([]);
  const [adHocAssetPickerOpen, setAdHocAssetPickerOpen] = useState(false);
  const [adHocAssetIntegrating, setAdHocAssetIntegrating] = useState(false);

  const stripHtmlForDirection = (value) => {
    const html = String(value || '').replace(/&nbsp;/g, ' ');
    if (typeof window !== 'undefined' && window.document) {
      const tmp = window.document.createElement('div');
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || '').trim();
    }
    return html.replace(/<[^>]*>/g, ' ').trim();
  };
  const hasMeaningfulArabicText = (value) => {
    const plain = stripHtmlForDirection(value);
    const withoutUrls = plain.replace(URL_TEXT_REGEX, ' ');
    const matches = withoutUrls.match(new RegExp(ARABIC_TEXT_REGEX.source, 'g'));
    return (matches?.length || 0) >= MIN_ARABIC_CHARS_FOR_RTL;
  };
  const draftLanguage = String(
    draft?.language || draft?.message_language || draft?.locale || draft?.metadata?.language || ''
  ).toLowerCase();
  const isArabicDraftLanguage = draftLanguage.includes('arabic') || draftLanguage === 'ar';
  const isArabicDetected = hasMeaningfulArabicText(`${subject} ${body}`);
  const isRtlMessage = isArabicDraftLanguage || isArabicDetected;

  useEffect(() => {
    if (draft) {
      setSubject(draft.subject || '');
      const bodyContent = resolveOutboxBodyHtml(draft);
      setBody(bodyContent);
      setOriginalBody(bodyContent);

      const localDraftId = draft.stepId || draft.id;
      const isTemporary = draft.is_temporary || localDraftId?.startsWith('draft-');
      const isHistoryItem = draft.is_history || draft.status === 'sent' || draft.status === 'manual_completion';
      const isAdHocScheduledItem = isAdHocScheduledOutboxDraft(draft);

      let cancelled = false;
      if (
        !isTemporary &&
        !isHistoryItem &&
        !isAdHocScheduledItem &&
        localDraftId &&
        outboxBodyHtmlNeedsFetch(draft) &&
        tenantId &&
        userId
      ) {
        (async () => {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/outbox/${encodeURIComponent(localDraftId)}?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`,
              { credentials: 'include' },
            );
            if (!response.ok || cancelled) return;
            const data = await response.json();
            const html = resolveOutboxBodyHtml(data);
            if (html && !cancelled) {
              setBody(html);
              setOriginalBody(html);
            }
          } catch {
            // Keep list-row body if detail fetch fails
          }
        })();
      }
      
      // Skip API calls for temporary drafts, history items, and ad-hoc scheduled queue rows.
      if (!isTemporary && !isHistoryItem && !isAdHocScheduledItem) {
        loadEvidence();
        checkUndoAvailable();
      } else {
        setEvidence(evidenceFromGenerationPayload(draft));
      }
      
      loadPersonData();
      
      // Check for previous touch if stage 2+
      // Normalize sequence_id with defensive fallbacks
      const sequenceId = draft.sequence_id 
        || draft.sequenceId 
        || draft.sequence?.id 
        || null;
      
      // Normalize stage with defensive fallbacks (including touch_number)
      const currentStage = draft.stage 
        || draft.stage_no 
        || draft.stageNo
        || draft.currentStage
        || draft.touch_number
        || draft.touchNumber
        || null;
      
      console.log('📞 Stage check:', { 
        currentStage, 
        sequence_id: sequenceId,
        hasStage: !!currentStage,
        hasSequenceId: !!sequenceId
      });
      
      if (currentStage && currentStage > 1 && sequenceId) {
        checkPreviousTouch(currentStage);
      }

      return () => {
        cancelled = true;
      };
    }
  }, [draft, tenantId, userId]);

  useEffect(() => {
    const temp = draft?.is_temporary || draftId?.startsWith('draft-');
    const personKeys = orderedPersonKeysForAssets(draft);
    const uid = userId || contextUser?.id || contextUser?.user_id;

    if (!temp || personKeys.length === 0 || !uid || !tenantId) {
      setAdHocAvailableAssets([]);
      return;
    }

    for (const k of personKeys) {
      if (availableAssetsCacheRef.current.has(k)) {
        setAdHocAvailableAssets(availableAssetsCacheRef.current.get(k));
        return;
      }
    }

    let cancelled = false;

    (async () => {
      try {
        let assets = [];
        let hitOk = false;
        for (const personKey of personKeys) {
          if (cancelled) return;
          const url = `${API_BASE_URL}/api/personas/${encodeURIComponent(personKey)}/available-assets?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(uid)}`;
          const res = await fetch(url, {
            credentials: 'include',
            headers: {
              'x-tenant-id': String(tenantId),
              'x-user-id': String(uid),
            },
          });
          if (!res.ok) continue;
          hitOk = true;
          const data = await res.json();
          assets = Array.isArray(data?.assets) ? data.assets.filter((a) => a?.url && String(a.url).trim()) : [];
          personKeys.forEach((alias) => {
            availableAssetsCacheRef.current.set(alias, assets);
          });
          break;
        }
        if (!hitOk) {
          personKeys.forEach((alias) => {
            availableAssetsCacheRef.current.set(alias, []);
          });
        }
        if (!cancelled) setAdHocAvailableAssets(assets);
      } catch (e) {
        console.error('available-assets fetch failed:', e);
        if (!cancelled) setAdHocAvailableAssets([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    draftId,
    tenantId,
    userId,
    contextUser?.id,
    contextUser?.user_id,
    draft?.is_temporary,
    draft?.person_id,
    draft?.persona_id,
    draft?.personId,
    draft?.person?.person_id,
    draft?.person?.persona_id,
    draft?.person?.id,
  ]);

  const handleInsertAdHocAsset = async (asset) => {
    if (!asset?.url || adHocAssetIntegrating) return;
    const label = String(asset.name || '').trim() || 'Link';
    const integrateChannel = normalizeIntegrateAssetChannel(
      buildOutboxChannelRecordingPayload(channelDraft).channel,
    );

    const personKeys = orderedPersonKeysForAssets(draft);
    const personId = personKeys[0];
    const uid = userId || contextUser?.user_id || contextUser?.id;
    if (!personId || !tenantId) {
      notify({
        title: 'Cannot add asset',
        description: 'Missing person or tenant. Try reopening the draft.',
        variant: 'destructive',
      });
      return;
    }

    setAdHocAssetIntegrating(true);
    try {
      const recipientFirstName =
        fullPerson?.first_name ||
        draft?.person?.first_name ||
        (String(draft?.person_name || draft?.person?.name || '').trim().split(/\s+/)[0] || undefined);
      const companyName =
        String(draft?.account_name || draft?.account?.name || draft?.account?.account_name || '').trim() ||
        undefined;
      const language =
        String(draft?.language || draft?.message_language || 'English').trim() || 'English';

      const { ok, data } = await integratePersonaAsset({
        personId,
        tenantId,
        userId: uid,
        bodyHtml: bodyRef.current,
        asset: { type: asset.type, name: label, url: asset.url },
        recipientFirstName,
        companyName,
        language,
        channel: integrateChannel,
      });

      if (ok && data?.integrated && typeof data.body_html === 'string') {
        setBody(data.body_html);
        return;
      }
      if (data?.reason === 'asset_already_present') {
        notify({ title: 'That link is already in the message.', variant: 'info' });
        return;
      }
      notify({
        title: "Couldn't add that link cleanly — try again or paste manually.",
        variant: 'default',
      });
    } catch {
      notify({
        title: "Couldn't add that link cleanly — try again or paste manually.",
        variant: 'default',
      });
    } finally {
      setAdHocAssetIntegrating(false);
    }
  };

  const checkUndoAvailable = async () => {
    // For temporary drafts, undo is handled via local state
    if (draftId?.startsWith('draft-') || draft?.is_temporary) {
      setCanUndo(undoHistory.length > 0);
      return;
    }
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/${draftId}?tenant_id=${tenantId}&user_id=${encodeURIComponent(userId)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setCanUndo(!!data.prev_body_html);
      }
    } catch (e) {
      setCanUndo(false);
    }
  };

  const loadPersonData = async () => {
    try {
      // Get persona_id from various fields - personas table is source of truth
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      
      // For history / ad-hoc scheduled queue rows, use payload data — never hide on fetch failure.
      const isHistoryItem = draft.is_history || draft.status === 'sent' || draft.status === 'manual_completion';
      const isAdHocScheduledItem = isAdHocScheduledOutboxDraft(draft);
      
      if (isHistoryItem || isAdHocScheduledItem) {
        console.log('📜 History/scheduled item - using provided person data directly');
        // Build person from whatever we have in the draft
        const historyPerson = {
          id: personaId,
          person_id: personaId,
          name: draft.person?.name || draft.person_name,
          full_name: draft.person?.full_name || draft.person_name,
          first_name: draft.person?.first_name || draft.person_name?.split(' ')[0],
          last_name: draft.person?.last_name || draft.person_name?.split(' ').slice(1).join(' '),
          email: draft.person?.email || draft.person_email,
          linkedin_url: draft.person?.linkedin_url || draft.linkedin_url,
          phone: draft.person?.phone || draft.phone,
          role_title: draft.person?.role_title
        };
        console.log('📜 History person data:', historyPerson);
        setFullPerson(historyPerson);
        return;
      }
      
      // For non-history items, ALWAYS fetch from personas table (source of truth)
      // Only fallback to draft.person if API call fails
      if (personaId) {
        console.log('🔍 Loading person data from personas table (source of truth) for persona_id:', personaId, 'tenant:', tenantId);
        
        try {
          // Fetch current data from personas table - always use this as source of truth
          const response = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          
          if (response.ok) {
            const dbPerson = await response.json();
            console.log('✅ Found person data from personas table:', dbPerson);
            
            // Use personas table as source of truth - always prioritize database values
            const personFromPersonas = {
              id: personaId,
              person_id: personaId,
              persona_id: personaId,
              full_name: dbPerson.full_name || draft.person?.full_name || draft.person_name,
              first_name: dbPerson.first_name || draft.person?.first_name,
              last_name: dbPerson.last_name || draft.person?.last_name,
              name: dbPerson.full_name || dbPerson.name || draft.person?.name || draft.person_name,
              // ALWAYS use personas table for contact data (source of truth)
              email: dbPerson.email || null,
              linkedin_url: dbPerson.linkedin_url || null,
              linkedin_connected: dbPerson.linkedin_connected,
              phone: dbPerson.phone || null,
              // Other fields can fallback to draft data
              role_title: dbPerson.role_title || draft.person?.role_title,
              profile_photo_url: dbPerson.profile_photo_url || draft.person?.profile_photo_url
            };
            
            console.log('✅ Person data from personas table (source of truth):', personFromPersonas);
            setFullPerson(personFromPersonas);
            return;
          } else {
            console.warn('⚠️ Person API returned:', response.status, '- falling back to draft data');
          }
        } catch (apiError) {
          console.warn('⚠️ Person API error:', apiError, '- falling back to draft data');
        }
      }
      
      // Fallback: use whatever we have from draft context (only if personas fetch failed)
      if (draft.person || draft.person_name) {
        console.log('⚠️ Using draft context as fallback (personas table fetch failed)');
        setFullPerson({
          id: personaId,
          person_id: personaId,
          persona_id: personaId,
          name: draft.person?.name || draft.person_name,
          full_name: draft.person?.full_name || draft.person_name,
          first_name: draft.person?.first_name || draft.person_name?.split(' ')[0],
          last_name: draft.person?.last_name || draft.person_name?.split(' ').slice(1).join(' '),
          email: draft.person?.email || draft.person_email,
          linkedin_url: draft.person?.linkedin_url || draft.linkedin_url,
          phone: draft.person?.phone || draft.phone,
          role_title: draft.person?.role_title
        });
      }
    } catch (error) {
      console.error('Error loading person data:', error);
      // Still set whatever we have from draft as last resort
      if (draft.person || draft.person_name) {
        setFullPerson({
          name: draft.person?.name || draft.person_name,
          full_name: draft.person?.full_name || draft.person_name,
          email: draft.person?.email || draft.person_email,
          linkedin_url: draft.person?.linkedin_url || draft.linkedin_url,
          phone: draft.person?.phone || draft.phone
        });
      }
    }
  };

  useEffect(() => {
    if (contextUser?.preferred_gmail_alias) {
      setPreferredAlias(contextUser.preferred_gmail_alias);
    } else if (contextUser?.preferred_outlook_alias) {
      setPreferredAlias(contextUser.preferred_outlook_alias);
    } else {
      setPreferredAlias(null);
    }
  }, [contextUser?.preferred_gmail_alias, contextUser?.preferred_outlook_alias]);

  const getConnectedEmailProviderTokens = () =>
    resolveEmailProviderTokens({
      tenantId,
      userId,
      preferredOutlookAlias: contextUser?.preferred_outlook_alias,
      preferredGmailAlias: contextUser?.preferred_gmail_alias,
    });

  const buildProviderAwareError = async (response, provider) => {
    const raw = await response.text();
    let payload = null;
    try {
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      payload = null;
    }

    if (payload?.needs_auth) {
      const targetProvider = String(payload?.error || '').toLowerCase().includes('gmail')
        ? 'gmail'
        : String(payload?.error || '').toLowerCase().includes('outlook')
          ? 'outlook'
          : provider;
      const authUrl = payload?.auth_url;
      if (authUrl && targetProvider === provider) {
        return `Your ${provider === 'outlook' ? 'Outlook' : 'Gmail'} session expired. Reconnect it in Settings and try again.`;
      }
      if (targetProvider !== provider) {
        return `Backend rejected ${provider === 'outlook' ? 'Outlook' : 'Gmail'} tokens and asked for ${targetProvider}. This is a backend provider-routing issue.`;
      }
    }

    return `Failed: ${raw}`;
  };

  const checkPreviousTouch = async (currentStage) => {
    const sequenceId = draft.sequence_id || draft.sequenceId || draft.sequence?.id;
    console.log('📞 Checking previous touch:', { currentStage, sequence_id: sequenceId });
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/previous-touch?tenant_id=${tenantId}&sequence_id=${sequenceId}&stage_no=${currentStage}&user_id=${encodeURIComponent(userId)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('📞 Previous touch result:', data);
        if (data.touch) {
          setPreviousTouch(data.touch);
        }
      }
    } catch (e) {
      console.error('Error checking previous touch:', e);
    }
  };

  const loadEvidence = async () => {
    const fromDraft = evidenceFromGenerationPayload(draft);
    // Temporary / ad-hoc drafts skip /api/outbox/:id/evidence — use generate-message payload.
    if (draftId?.startsWith('draft-') || draft?.is_temporary) {
      setEvidence(fromDraft);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/${draftId}/evidence?tenant_id=${tenantId}&user_id=${encodeURIComponent(userId)}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const merged = evidenceFromGenerationPayload({
          ...data,
          context_signals: data.context_signals || draft?.context_signals,
          used_signals: data.used_signals || draft?.used_signals,
        });
        setEvidence(evidenceHasContent(merged) ? merged : fromDraft);
      } else {
        setEvidence(fromDraft);
      }
    } catch (error) {
      console.error('Error loading evidence:', error);
      setEvidence(fromDraft);
    }
  };

  const insertVariable = (variable) => {
    const variableText = `{{${variable}}}`;
    setBody(prev => prev + variableText);
    notify({
      title: 'Variable inserted',
      description: `Added {{${variable}}} to your message`,
    });
  };

  const insertEvidence = (text) => {
    navigator.clipboard.writeText(text);
    notify({
      title: 'Copied to clipboard',
      description: 'Paste into your message where needed',
    });
  };

  const handleCopyEmail = async () => {
    if (personEmail) {
      try {
        await navigator.clipboard.writeText(personEmail);
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
        notify({
          title: 'Copied to clipboard',
          description: `Email address copied: ${personEmail}`,
        });
      } catch (error) {
        console.error('Failed to copy email:', error);
        notify({
          title: 'Copy failed',
          description: 'Please try again',
          variant: 'destructive'
        });
      }
    }
  };

  const handleCopyPhone = async () => {
    if (personPhone) {
      try {
        await navigator.clipboard.writeText(personPhone);
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
        notify({
          title: 'Copied to clipboard',
          description: `Phone number copied: ${personPhone}`,
        });
      } catch (error) {
        console.error('Failed to copy phone:', error);
        notify({
          title: 'Copy failed',
          description: 'Please try again',
          variant: 'destructive'
        });
      }
    }
  };

  // Create provider draft (Gmail or Outlook)
  const handleCreateDraft = async () => {
    setShowSendOptions(false);
    setIsSending(true);
    
    try {
      const { provider } = await getConnectedEmailProviderTokens();

      // Get persona_id - primary reference to personas table
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      
      // ALWAYS fetch current email from personas table (source of truth) at send time
      // Use fullPerson first (already loaded from personas table), then fallback to other sources
      let currentEmail = fullPerson?.email || personEmail || draft.person?.email;
      if (personaId && !currentEmail) {
        try {
          const personaResponse = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          if (personaResponse.ok) {
            const persona = await personaResponse.json();
            currentEmail = persona.email;
            console.log('✅ Fetched current email from personas table:', currentEmail);
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch email from personas table, using fallback:', err);
        }
      }

      if (!currentEmail) {
        throw new Error('Recipient email not found');
      }

      const requestBody = {
        tenant_id: tenantId,
        user_id: userId,
        subject,
        body_html: body,
        send_method: 'draft',
        from_email: preferredAlias,
        include_signature: true,
        // persona_id is the primary reference (source of truth)
        persona_id: personaId,
        // recipient_email is a fallback/cache, but persona_id takes precedence
        recipient_email: currentEmail,
        is_temporary: draft?.is_temporary || draftId?.startsWith('draft-'),
        person_id: personaId, // Keep for backward compatibility
        person_name: draft.person?.name || fullPerson?.name,
        account_id: draft.account?.id,
        account_name: draft.account?.name,
        channel: draft.channel,
        ...pickUsedSignalsAndFocus(draft),
      };

      console.log('📧 Sending draft request:', requestBody);

      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(await buildProviderAwareError(response, provider));
      }

      notify({
        title: `Draft created in ${provider === 'outlook' ? 'Outlook' : 'Gmail'}!`,
        description: `Review it in your ${provider === 'outlook' ? 'Outlook' : 'Gmail'} drafts folder`,
      });
      
      // Track Gmail draft creation
      logAccountInteraction({
        tenantId,
        userId,
        accountId: extractAccountId(draft.account),
        accountName: draft.account?.name,
        personId: personaId,
        personName: draft.person?.name || fullPerson?.name,
        stepId: extractStepId(draft),
        channel: 'email',
        source: 'outbox',
        interactionType: 'exported',
        exported: true,
        touchNumber: stageNo
      }).catch(err => console.error('Analytics error:', err));

      if (onSent) onSent(draftId);
      onClose();

    } catch (error) {
      console.error('Error creating draft:', error);
      notify({
        title: 'Failed to create draft',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const buildAdHocSendPayload = ({
    provider,
    personaId,
    currentEmail,
    sendMethod,
    scheduledAtISO,
  }) => ({
    tenant_id: tenantId,
    user_id: userId,
    email_provider: provider,
    provider,
    subject,
    body_html: body,
    send_method: sendMethod,
    ...(scheduledAtISO ? { scheduled_at: scheduledAtISO } : {}),
    from_email: preferredAlias,
    include_signature: true,
    // persona_id is the primary reference (source of truth)
    persona_id: personaId,
    person_id: personaId, // Keep for backward compatibility
    person_name: draft.person?.name || draft.person_name || fullPerson?.name,
    // Recipient fields required by BE for temporary/ad-hoc drafts
    recipient_email: currentEmail,
    person_email: currentEmail,
    // Person identity/contact
    person_linkedin_url: draft.linkedin_url || draft.person?.linkedin_url || fullPerson?.linkedin_url,
    linkedin_url: draft.linkedin_url || draft.person?.linkedin_url || fullPerson?.linkedin_url,
    person_phone: draft.phone || draft.person?.phone || fullPerson?.phone,
    phone: draft.phone || draft.person?.phone || fullPerson?.phone,
    archetype: draft.archetype || draft.person?.archetype || fullPerson?.archetype,
    // Account context
    account_id: draft.account?.id || draft.account_id || draft.account?.account_id,
    account_name: draft.account?.name || draft.account_name || draft.account?.account_name,
    // Channel context
    channel: draft.channel || 'email',
    is_temporary: draft?.is_temporary || draftId?.startsWith('draft-'),
    ...pickUsedSignalsAndFocus(draft),
  });

  // Send Now
  const handleSendNow = async () => {
    setShowSendOptions(false);
    setIsSending(true);
    
    try {
      const { provider } = await getConnectedEmailProviderTokens();

      // Get persona_id - primary reference to personas table
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      
      // ALWAYS fetch current email from personas table (source of truth) at send time
      // Never rely on cached recipient_email for actual sending
      let currentEmail = fullPerson?.email || personEmail || draft.person?.email;
      if (personaId && !currentEmail) {
        try {
          const personaResponse = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          if (personaResponse.ok) {
            const persona = await personaResponse.json();
            currentEmail = persona.email;
            console.log('✅ Fetched current email from personas table at send time:', currentEmail);
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch email from personas table, using fallback:', err);
        }
      }

      if (!currentEmail) {
        throw new Error('Recipient email not found');
      }

      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildAdHocSendPayload({
          provider,
          personaId,
          currentEmail,
          sendMethod: 'now',
        }))
      });

      if (!response.ok) {
        throw new Error(await buildProviderAwareError(response, provider));
      }

      notify({
        title: 'Email sent!',
        description: `Sent to ${draft.person?.name || fullPerson?.name}`,
      });
      
      // Track immediate email send
      logAccountInteraction({
        tenantId,
        userId,
        accountId: extractAccountId(draft.account),
        accountName: draft.account?.name,
        personId: personaId,
        personName: draft.person?.name || fullPerson?.name,
        stepId: extractStepId(draft),
        channel: 'email',
        source: 'outbox',
        interactionType: 'sent',
        exported: false,
        touchNumber: stageNo
      }).catch(err => console.error('Analytics error:', err));

      if (onSent) onSent(draftId);
      onClose();

    } catch (error) {
      console.error('Error sending:', error);
      notify({
        title: 'Failed to send',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  // Schedule Send
  const handleScheduleSend = async () => {
    setShowSendOptions(false);
    setIsSending(true);
    
    try {
      const { provider } = await getConnectedEmailProviderTokens();

      const scheduledDate = draft.estimated_send || draft.due_at || new Date();

      // Get persona_id - primary reference to personas table
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      
      // ALWAYS fetch current email from personas table (source of truth) at schedule time
      // Never rely on cached recipient_email
      let currentEmail = fullPerson?.email || personEmail || draft.person?.email;
      if (personaId && !currentEmail) {
        try {
          const personaResponse = await fetch(
            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
            { credentials: 'include' }
          );
          if (personaResponse.ok) {
            const persona = await personaResponse.json();
            currentEmail = persona.email;
            console.log('✅ Fetched current email from personas table at schedule time:', currentEmail);
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch email from personas table, using fallback:', err);
        }
      }

      if (!currentEmail) {
        throw new Error('Recipient email not found');
      }

      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildAdHocSendPayload({
          provider,
          personaId,
          currentEmail,
          sendMethod: 'scheduled',
          scheduledAtISO: new Date(scheduledDate).toISOString(),
        }))
      });

      if (!response.ok) {
        throw new Error(await buildProviderAwareError(response, provider));
      }

      notify({
        title: 'Email scheduled!',
        description: `Will send at ${format(new Date(scheduledDate), 'PPP p')}`,
      });

      // Track scheduled email send
      logAccountInteraction({
        tenantId,
        userId,
        accountId: extractAccountId(draft.account),
        accountName: draft.account?.name,
        personId: personaId,
        personName: draft.person?.name || fullPerson?.name,
        stepId: extractStepId(draft),
        channel: 'email',
        source: 'outbox',
        interactionType: 'scheduled',
        exported: false,
        touchNumber: stageNo
      }).catch(err => console.error('Analytics error:', err));

      if (onSent) onSent(draftId);
      onClose();

    } catch (error) {
      console.error('Error scheduling:', error);
      notify({
        title: 'Failed to schedule',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleMarkPreviousResponse = async (touchId, responseType) => {
    try {
      await fetch(`${API_BASE_URL}/api/outbox/${touchId}/mark-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tenant_id: tenantId,
          response_type: responseType,
          user_id: userId
        })
      });
      setPreviousTouch(null);
      
      notify({
        title: 'Response logged',
        description: responseType === 'positive' ? 'Marked as replied' : 'Marked as no response',
      });
    } catch (e) {
      console.error('Error marking response:', e);
    }
  };

  const handleMarkAsDone = async () => {
    try {
      // Get persona_id - primary reference to personas table
      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
      const channelPayload = buildOutboxChannelRecordingPayload(channelDraft);

      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          tenant_id: tenantId, 
          user_id: userId,
          send_method: 'done',
          subject: subject || '',
          body_html: body || '',
          from_email: null,
          include_signature: false,
          // persona_id is the primary reference (source of truth)
          persona_id: personaId,
          // Add all person/account fields for sent_messages logging
          // Use current data from personas table (fullPerson) if available, otherwise fallback
          person_id: personaId, // Keep for backward compatibility
          person_name: draft.person_name || draft.person?.name || draft.person?.full_name || fullPerson?.name,
          person_email: fullPerson?.email || personEmail,
          person_linkedin_url: fullPerson?.linkedin_url || personLinkedIn,
          person_phone: fullPerson?.phone || personPhone,
          account_id: draft.account_id || draft.account?.id,
          account_name: draft.account_name || draft.account?.name || draft.account?.account_name,
          archetype: draft.archetype,
          is_temporary: isTemporary,
          ...channelPayload,
          ...pickUsedSignalsAndFocus(draft),
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('📧 Mark as done failed:', errorText);
        throw new Error(`Failed to mark as done: ${errorText}`);
      }

      notify({
        title: 'Marked as done',
        description: `Message to ${draft.person?.name || fullPerson?.name} marked as complete`,
      });

      if (isLinkedInOutboxChannel(channelDraft)) {
        logAccountInteraction({
          tenantId,
          userId,
          accountId: extractAccountId(draft.account),
          accountName: draft.account?.name,
          personId: personaId,
          personName: draft.person?.name || fullPerson?.name,
          stepId: extractStepId(channelDraft),
          channel: channelPayload.channel,
          source: 'outbox',
          interactionType: 'completed',
          touchNumber: stageNo,
          metadata: {
            linkedin_sub_type: channelPayload.linkedin_sub_type,
            is_connection_request: channelPayload.is_connection_request,
          },
        }).catch((err) => console.error('Analytics error:', err));
      }

      if (onSent) onSent(draftId);
      onClose();
    } catch (error) {
      console.error('Error marking as done:', error);
      notify({
        title: 'Failed to mark as done',
        description: error.message || 'Please try again',
        variant: 'destructive'
      });
    }
  };

  const handleCancelScheduledSend = async () => {
    if (!draftId || !tenantId || !userId) return;
    setIsSending(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/${encodeURIComponent(draftId)}?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || 'Failed to cancel scheduled send');
      }
      notify({
        title: 'Scheduled send cancelled',
        description: `Removed scheduled message to ${draft.person?.name || fullPerson?.name || 'recipient'}`,
      });
      if (onSent) onSent(draftId);
      onClose();
    } catch (error) {
      console.error('Error cancelling scheduled send:', error);
      notify({
        title: 'Could not cancel scheduled send',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    // For LinkedIn, just open the profile in a new tab
    if (isLinkedInDraft) {
      const linkedInUrl = fullPerson?.linkedin_url || draft.person?.linkedin_url;
      if (linkedInUrl) {
        window.open(linkedInUrl, '_blank');
        notify({
          title: 'Opening LinkedIn',
          description: `Opening ${draft.person?.name}'s LinkedIn profile`,
        });
        
        const channelPayload = buildOutboxChannelRecordingPayload(channelDraft);
        // Track LinkedIn open for analytics (but don't mark as sent)
        logAccountInteraction({
          tenantId,
          userId,
          accountId: extractAccountId(draft.account),
          accountName: draft.account?.name,
          personId: extractPersonId(draft.person),
          personName: draft.person?.name,
          stepId: extractStepId(channelDraft),
          channel: channelPayload.channel,
          source: 'outbox',
          interactionType: 'opened',
          touchNumber: stageNo,
          metadata: {
            linkedin_sub_type: channelPayload.linkedin_sub_type,
            is_connection_request: channelPayload.is_connection_request,
          },
        }).catch(err => console.error('Analytics error:', err));
        
        // Close drawer but don't remove from outbox
        onClose();
      } else {
        notify({
          title: 'LinkedIn URL missing',
          description: 'No LinkedIn profile URL found for this person',
          variant: 'destructive'
        });
      }
      return;
    }

    // For call scripts, just close the drawer (don't mark as completed)
    if (draft.channel === 'call') {
      onClose();
      return;
    }
  };

  const stageNo = draft.stage 
    || draft.stage_no 
    || draft.stageNo
    || draft.currentStage
    || draft.touch_number
    || draft.touchNumber
    || null;

  const person = fullPerson || draft.person || {};
  const personEmail = person.email || draft.person?.email || draft.person_email || draft.personEmail;
  const personLinkedIn = person.linkedin_url || draft.person?.linkedin_url || draft.linkedin_url || draft.linkedinUrl;
  const personPhone = person.phone || draft.person?.phone || draft.phone;

  const trackMessageExported = () => {
    const personaId =
      draft?.persona_id ||
      draft?.person_id ||
      draft?.personId ||
      draft?.person?.persona_id ||
      draft?.person?.person_id ||
      draft?.person?.id;
    const isDiy = Boolean(
      draft?.diy_playbook_id ||
        draft?.sequence?.diy_playbook_id ||
        draft?.source === 'diy_playbook',
    );

    logAccountInteraction({
      tenantId,
      userId,
      accountId: extractAccountId(draft.account) || draft.account_id,
      accountName: draft.account?.name || draft.account_name,
      personId: personaId,
      personName: draft.person?.name || fullPerson?.name,
      stepId: extractStepId(draft),
      playbookId: draft?.diy_playbook_id || draft?.sequence?.diy_playbook_id || draft?.playbook_id,
      channel: draft.channel,
      source: isDiy ? 'diy' : 'outbox',
      interactionType: 'exported',
      exported: true,
      touchNumber: stageNo,
    }).catch((err) => console.error('Analytics error:', err));
  };

  const isLinkedInDraft = isLinkedInOutboxChannel(channelDraft);
  const isLinkedInConnectRequest = isLinkedInConnectionRequest(channelDraft);

  const getButtonConfig = () => {
    if (isLinkedInDraft) {
      return {
        disabled: !personLinkedIn,
        text: isLinkedInConnectRequest ? 'Open LinkedIn to connect' : 'Open LinkedIn',
        icon: <Linkedin className="w-4 h-4 mr-2" />
      };
    }
    if (draft.channel === 'call') {
      return {
        disabled: !body.trim(),
        text: 'Mark Complete',
        icon: <CheckCircle2 className="w-4 h-4 mr-2" />
      };
    }
    return {
      disabled: isSending || !subject.trim() || !body.trim(),
      text: isSending ? 'Sending...' : 'Send Now',
      icon: isSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />
    };
  };

  const buttonConfig = getButtonConfig();

  // ============================================
  // AI EDIT HANDLERS
  // ============================================

  const handleGenerateVariants = async () => {
    setIsGeneratingVariants(true);
    setAiVariants([]);
    setSelectedVariant(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/ai-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId,
          action: aiEditAction,
          tone: aiEditTone || undefined,
          language: aiEditLanguage || undefined,
          instruction: aiEditInstruction || undefined,
          base_subject: subject,
          base_body_html: body,
          channel: draft?.channel || 'email'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to generate variants');
      }

      const data = await response.json();
      setAiVariants(data.variants || []);

      if (data.variants?.length > 0) {
        notify({
          title: 'Variants ready',
          description: 'Select a variant to apply',
        });
      }
      
    } catch (error) {
      console.error('Error generating variants:', error);
      notify({
        title: 'Failed to generate variants',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsGeneratingVariants(false);
    }
  };

  const handleApplyVariant = async (variant, index) => {
    try {
      // Save current state to undo history before applying
      setUndoHistory(prev => [...prev, { subject, body }]);

      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/apply-variant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tenant_id: tenantId,
          user_id: userId,
          subject: variant.subject,
          body_html: variant.body_html,
          qaStatus: variant.qaStatus,
          qaIssues: variant.qaIssues
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to apply variant');
      }

      setSubject(variant.subject);
      setBody(variant.body_html);
      setOriginalBody(body);
      setCanUndo(true);
      setAiVariants([]);
      setSelectedVariant(null);
      setShowAIEdit(false);
      
      setAiEditAction('shorten');
      setAiEditTone('');
      setAiEditLanguage('');
      setAiEditInstruction('');

      notify({
        title: 'Variant applied',
        description: `Applied Variant ${index === 0 ? 'A' : 'B'}. You can undo if needed.`,
      });
      
    } catch (error) {
      console.error('Error applying variant:', error);
      notify({
        title: 'Failed to apply',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleUndo = async () => {
    // Client-side undo for temporary drafts
    if (draftId?.startsWith('draft-') || draft?.is_temporary) {
      if (undoHistory.length > 0) {
        const prevState = undoHistory[undoHistory.length - 1];
        setSubject(prevState.subject);
        setBody(prevState.body);
        setUndoHistory(prev => prev.slice(0, -1));
        setCanUndo(undoHistory.length > 1);
        notify({
          title: 'Undone',
          description: 'Reverted to previous version',
        });
      } else {
        notify({
          title: 'Nothing to undo',
          description: 'No previous version available',
          variant: 'destructive'
        });
      }
      return;
    }

    // Server-side undo for real steps
    try {
      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId, user_id: userId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to undo');
      }

      const data = await response.json();
      setSubject(data.subject || subject);
      setBody(data.body_html);
      setCanUndo(false);

      notify({
        title: 'Undone',
        description: 'Reverted to previous version',
      });
      
    } catch (error) {
      console.error('Error undoing:', error);
      notify({
        title: 'Failed to undo',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const renderDiff = (oldText, newText) => {
    const stripHtml = (html) => html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
      
    const oldPlain = stripHtml(oldText);
    const newPlain = stripHtml(newText);
    const differences = diffWords(oldPlain, newPlain);
    
    return (
      <div className="text-sm whitespace-pre-wrap leading-relaxed">
        {differences.map((part, i) => (
          <span
            key={i}
            className={
              part.added 
                ? 'bg-green-100 text-green-800 px-0.5 rounded' 
                : part.removed 
                  ? 'bg-red-100 text-red-800 line-through px-0.5 rounded' 
                  : ''
            }
          >
            {part.value}
          </span>
        ))}
      </div>
    );
  };

  const showAdHocAssetBar = !isSent && isTemporary && adHocAvailableAssets.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      <div className="relative ml-auto w-full md:max-w-4xl bg-white shadow-2xl flex flex-col max-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b bg-gray-50 gap-3 md:gap-0">
          <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
            <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0">
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                <h2 className="font-semibold text-sm md:text-base text-[#0A1A2F]">
                  {draft.channel === 'email' ? '📧 Email' : 
                   isLinkedInDraft ? '💼 LinkedIn' : 
                   draft.channel === 'call' ? '📞 Call' :
                   'Draft'}
                </h2>
                {isLinkedInDraft && (
                  <LinkedInSubtypeBadge
                    source={channelDraft}
                    debugContext="drawer-header"
                  />
                )}
                {stageNo && (
                  <Badge variant="secondary" className="text-[10px] md:text-xs px-1.5 py-0.5">
                    Touch {stageNo}
                  </Badge>
                )}
                {statusBadge && (
                  <Badge
                    className={`${statusBadge.className} text-[10px] md:text-xs px-1.5 py-0.5 border`}
                  >
                    {statusBadge.showSpinner && (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                    )}
                    {statusBadge.label}
                  </Badge>
                )}
              </div>
              <p className="text-xs md:text-sm text-[#5C6B7A] truncate">
                {draft?.person?.name}{person.role_title ? ` • ${person.role_title}` : ''} @ {draft?.account?.name}
              </p>
              {statusSubtext && (
                <p className="text-[10px] md:text-xs text-gray-500 mt-0.5">{statusSubtext}</p>
              )}
              {draft?.product_context && (
                <p className="text-xs text-[#00A3A3] mt-0.5 font-medium">
                  Product context: {draft.product_context}
                </p>
              )}
            </div>
          </div>
          
          {draft.channel === 'email' ? (
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
              {isAdHocScheduledDraft ? (
                <Button
                  variant="outline"
                  onClick={handleCancelScheduledSend}
                  disabled={isSending}
                  size="sm"
                  className="text-xs md:text-sm h-8 md:h-9 text-red-700 border-red-200 hover:bg-red-50"
                >
                  <X className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="hidden md:inline">Cancel scheduled send</span>
                  <span className="md:hidden">Cancel</span>
                </Button>
              ) : !isSent && !isHistoryItem && (
                <>
              <Button
                variant="outline"
                onClick={handleMarkAsDone}
                disabled={isSending}
                size="sm"
                className="text-xs md:text-sm h-8 md:h-9"
              >
                <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Mark Done</span>
                <span className="md:hidden">Done</span>
              </Button>
              <div className="relative">
                <Button 
                  onClick={() => setShowSendOptions(!showSendOptions)}
                  disabled={!subject.trim() || !body.trim() || isSending}
                  className="bg-[#00C2C2] hover:bg-[#00A3A3] text-xs md:text-sm h-8 md:h-9"
                  size="sm"
                >
                  <Send className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                  <span className="hidden md:inline">Send Options</span>
                  <span className="md:hidden">Send</span>
                </Button>

              {showSendOptions && (
                <>
                  <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setShowSendOptions(false)} />
                  <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-sm bg-white border rounded-lg shadow-xl z-50 md:absolute md:top-full md:left-auto md:right-0 md:translate-x-0 md:translate-y-0 md:w-64 md:mt-2 md:shadow-lg max-h-[80vh] overflow-y-auto">
                    <div className="py-2">
                    <button
                      onClick={handleCreateDraft}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-left hover:bg-gray-50 flex items-start gap-2 md:gap-3"
                    >
                      <Mail className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xs md:text-sm">Create Email Draft</p>
                        <p className="text-[10px] md:text-xs text-gray-500 break-words">Review in your connected inbox before sending</p>
                      </div>
                    </button>

                    <button
                      onClick={handleSendNow}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-left hover:bg-gray-50 flex items-start gap-2 md:gap-3 border-t"
                    >
                      <Send className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xs md:text-sm">Send Now</p>
                        <p className="text-[10px] md:text-xs text-gray-500 break-words">Send immediately via connected provider</p>
                      </div>
                    </button>

                    {!isTemporary && (
                      <button
                        onClick={handleScheduleSend}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 text-left hover:bg-gray-50 flex items-start gap-2 md:gap-3 border-t"
                      >
                        <Calendar className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0 text-blue-600" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs md:text-sm">Send at Scheduled Time</p>
                          {(draft.scheduled_send_at || draft.scheduledSendAt) ? (
                            <p className="text-[10px] md:text-xs text-blue-600 font-medium break-words">
                              {(() => {
                                try {
                                  const dateStr = draft.scheduled_send_at || draft.scheduledSendAt;
                                  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
                                  return format(date, 'EEE, MMM d \'at\' h:mm a');
                                } catch (e) {
                                  return 'Per comms schedule';
                                }
                              })()}
                            </p>
                          ) : (
                            <p className="text-[10px] md:text-xs text-gray-500 break-words">Based on your comms schedule</p>
                          )}
                        </div>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setShowSendOptions(false);
                        setShowSchedulePicker(true);
                      }}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-left hover:bg-gray-50 flex items-start gap-2 md:gap-3 border-t"
                    >
                      <Clock className="w-4 h-4 md:w-5 md:h-5 mt-0.5 flex-shrink-0 text-purple-600" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xs md:text-sm">Pick Custom Time</p>
                        <p className="text-[10px] md:text-xs text-gray-500 break-words">Choose a different date/time</p>
                      </div>
                    </button>
                  </div>
                </div>
                </>
              )}
              </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
              {!isSent && !isHistoryItem && (
                <>
              <Button
                variant="outline"
                onClick={handleMarkAsDone}
                disabled={isSending}
                size="sm"
                className="text-xs md:text-sm h-8 md:h-9"
              >
                <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Mark Done</span>
                <span className="md:hidden">Done</span>
              </Button>
              <Button 
                onClick={handleSend}
                disabled={buttonConfig.disabled}
                className="bg-[#00C2C2] hover:bg-[#00A3A3] text-xs md:text-sm h-8 md:h-9"
                size="sm"
              >
                {React.cloneElement(buttonConfig.icon, { className: 'w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2' })}
                {buttonConfig.text}
              </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Main Editor */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-3 md:p-6 space-y-3 md:space-y-4 flex-1 overflow-y-auto pb-20 md:pb-6">
              {/* Previous Touch Response Banner */}
              {previousTouch && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {isLinkedInOutboxChannel(previousTouch) ? 
                        <Linkedin className="w-4 h-4 text-[#0077B5] flex-shrink-0" /> : 
                        <Phone className="w-4 h-4 text-green-600 flex-shrink-0" />
                      }
                      <span className="text-sm truncate">
                        Did they reply to your{' '}
                        {isLinkedInOutboxChannel(previousTouch)
                          ? getChannelDisplayLabel(previousTouch)
                          : 'call'}
                        ?
                      </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button 
                        size="sm" 
                        className="h-7 bg-green-600 hover:bg-green-700 text-white px-3"
                        onClick={() => handleMarkPreviousResponse(previousTouch.id, 'positive')}
                      >
                        Yes
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-7 px-3"
                        onClick={() => handleMarkPreviousResponse(previousTouch.id, 'no_response')}
                      >
                        No
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-[#5C6B7A] flex-wrap">
                <Badge 
                  variant="outline" 
                  className={`${
                    isLinkedInDraft ? 'border-[#0077B5] text-[#0077B5] bg-[#0077B5]/5' :
                    draft.channel === 'email' ? 'border-blue-500 text-blue-500 bg-blue-50' :
                    'border-green-500 text-green-500 bg-green-50'
                  }`}
                >
                  {draft.channel === 'email' ? '📧 Email' : 
                   isLinkedInDraft ? '💼 LinkedIn' : 
                   '📞 Call'}
                </Badge>
                
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  {draft?.person?.name}{person.role_title ? ` • ${person.role_title}` : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {draft?.account?.name}
                </div>

                {draft.channel === 'email' && personEmail && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <a 
                      href={`mailto:${personEmail}`}
                      className="text-[#00C2C2] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {personEmail}
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCopyEmail();
                      }}
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                    >
                      {copiedEmail ? (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-500" />
                      )}
                    </Button>
                  </div>
                )}

                {/* Send timing — label follows status (Scheduled vs Suggested), not scheduled_send_at alone */}
                {!isTemporary && scheduleBanner && (
                  <div className={`flex items-center gap-2 ${scheduleBanner.containerClass}`}>
                    <Clock className={`w-4 h-4 shrink-0 ${scheduleBanner.iconClass}`} />
                    <div>
                      <span className={scheduleBanner.labelClass}>{scheduleBanner.label}</span>
                      <span className={scheduleBanner.dateClass}>{scheduleBanner.dateText}</span>
                    </div>
                  </div>
                )}

                {isLinkedInDraft && personLinkedIn && (
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4" />
                    <a 
                      href={personLinkedIn}
                      className="text-[#0077B5] hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      LinkedIn Profile
                    </a>
                  </div>
                )}

                {draft.channel === 'call' && personPhone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a 
                      href={`tel:${personPhone}`}
                      className="text-green-600 hover:underline"
                    >
                      {personPhone}
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCopyPhone();
                      }}
                      className="h-6 w-6 p-0 hover:bg-gray-100"
                    >
                      {copiedPhone ? (
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3 text-gray-500" />
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="text-sm font-medium text-[#0A1A2F] mb-2 block">
                  Subject Line
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject..."
                  disabled={isSent}
                  dir={isRtlMessage ? 'rtl' : 'ltr'}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#00C2C2] focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>

              {showAdHocAssetBar && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#00C2C2]/35 bg-[#ECFFFE] px-3 py-2">
                  <Link2 className="w-4 h-4 text-[#00A3A3] shrink-0" aria-hidden />
                  <span className="text-sm font-medium text-[#0A1A2F]">Suggested asset</span>
                  {adHocAvailableAssets.length === 1 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-[#00C2C2] text-[#0A1A2F] hover:bg-white"
                      disabled={adHocAssetIntegrating}
                      onClick={() => void handleInsertAdHocAsset(adHocAvailableAssets[0])}
                    >
                      {adHocAssetIntegrating ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin inline" aria-hidden />
                          Adding…
                        </>
                      ) : (
                        'Add asset'
                      )}
                    </Button>
                  ) : (
                    <Popover open={adHocAssetPickerOpen} onOpenChange={setAdHocAssetPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs border-[#00C2C2] text-[#0A1A2F] hover:bg-white"
                          disabled={adHocAssetIntegrating}
                        >
                          {adHocAssetIntegrating ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin inline" aria-hidden />
                              Adding…
                            </>
                          ) : (
                            <>
                              Add asset
                              <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
                            </>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2" align="start">
                        <div className="text-xs font-semibold text-gray-700 px-2 py-1.5">Insert asset</div>
                        <div className="max-h-56 overflow-y-auto space-y-1">
                          {adHocAvailableAssets.map((a, idx) => (
                            <button
                              key={`${a.url}-${idx}`}
                              type="button"
                              disabled={adHocAssetIntegrating}
                              className="w-full text-left flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
                              onClick={() => {
                                void handleInsertAdHocAsset(a);
                                setAdHocAssetPickerOpen(false);
                              }}
                            >
                              <Badge variant="outline" className="text-[10px] shrink-0 uppercase tracking-wide">
                                {a.type === 'product_link' ? 'Product link' : a.type === 'case_study' ? 'Case study' : (a.type || 'Asset')}
                              </Badge>
                              <span className="truncate">{a.name}</span>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}

              {/* AI Edit Panel */}
              {!isSent && (
              <div className="border rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
                  {/* Collapsed Header */}
                  <button
                    onClick={() => setShowAIEdit(!showAIEdit)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-sm text-purple-900">AI Edit</span>
                      {canUndo && (
                        <Badge variant="outline" className="text-xs border-purple-300 text-purple-700">
                          Can Undo
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {canUndo && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndo();
                          }}
                          className="text-xs h-7 text-purple-700 hover:text-purple-900 hover:bg-purple-100"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Undo
                        </Button>
                      )}
                      {showAIEdit ? (
                        <ChevronUp className="w-4 h-4 text-purple-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Panel */}
                  {showAIEdit && (
                    <div className="px-4 pb-4 space-y-4 border-t border-purple-100">
                      {/* Action Buttons */}
                      <div className="pt-3">
                        <Label className="text-xs text-gray-600 mb-2 block">Action</Label>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant={aiEditAction === 'shorten' ? 'default' : 'outline'}
                            onClick={() => setAiEditAction('shorten')}
                            className={`${aiEditAction === 'shorten' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                          >
                            <ArrowDownToLine className="w-3 h-3 mr-1" />
                            Shorten
                          </Button>
                          <Button
                            size="sm"
                            variant={aiEditAction === 'expand' ? 'default' : 'outline'}
                            onClick={() => setAiEditAction('expand')}
                            className={`${aiEditAction === 'expand' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                          >
                            <ArrowUpFromLine className="w-3 h-3 mr-1" />
                            Expand
                          </Button>
                          <Button
                            size="sm"
                            variant={aiEditAction === 'greeting' ? 'default' : 'outline'}
                            onClick={() => setAiEditAction('greeting')}
                            className={`${aiEditAction === 'greeting' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                          >
                            <UserIcon className="w-3 h-3 mr-1" />
                            Greeting
                          </Button>
                          <Button
                            size="sm"
                            variant={aiEditAction === 'language' ? 'default' : 'outline'}
                            onClick={() => setAiEditAction('language')}
                            className={`${aiEditAction === 'language' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                          >
                            <Globe className="w-3 h-3 mr-1" />
                            Language
                          </Button>
                        </div>
                      </div>

                      {/* Language Selector - only show when language action selected */}
                      {aiEditAction === 'language' && (
                        <div>
                          <Label className="text-xs text-gray-600 mb-2 block">Translate to</Label>
                          <Select value={aiEditLanguage} onValueChange={setAiEditLanguage}>
                            <SelectTrigger className="h-9 text-sm bg-white">
                              <SelectValue placeholder="Select language" />
                            </SelectTrigger>
                            <SelectContent>
                              {LANGUAGE_OPTIONS.map((language) => (
                                <SelectItem key={language.value} value={language.value}>
                                  {language.flag} {language.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Tone Dropdown */}
                      <div>
                        <Label className="text-xs text-gray-600 mb-2 block">Tone (optional)</Label>
                        <Select value={aiEditTone} onValueChange={setAiEditTone}>
                          <SelectTrigger className="h-9 text-sm bg-white">
                            <SelectValue placeholder="Keep current tone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={null}>Keep current</SelectItem>
                            <SelectItem value="Consultative">🤝 Consultative</SelectItem>
                            <SelectItem value="Challenger">💡 Challenger</SelectItem>
                            <SelectItem value="Direct">🎯 Direct</SelectItem>
                            <SelectItem value="Friendly">😊 Friendly</SelectItem>
                            <SelectItem value="Formal">👔 Formal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Free-text instruction */}
                      <div>
                        <Label className="text-xs text-gray-600 mb-2 block">Anything else? (optional)</Label>
                        <Input
                          value={aiEditInstruction}
                          onChange={(e) => setAiEditInstruction(e.target.value)}
                          placeholder="e.g., Make it more specific to their procurement role"
                          className="h-9 text-sm bg-white"
                        />
                      </div>

                      {/* Generate Button */}
                      <Button
                        onClick={handleGenerateVariants}
                        disabled={isGeneratingVariants || !body.trim()}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {isGeneratingVariants ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating variants...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate 2 Variants
                          </>
                        )}
                      </Button>

                      {/* Variants Display */}
                      {aiVariants.length > 0 && (
                        <div className="space-y-3 pt-3 border-t border-purple-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Select a variant:</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowDiff(!showDiff)}
                              className="text-xs h-7"
                            >
                              {showDiff ? 'Show Preview' : 'Show Changes'}
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            {aiVariants.map((variant, idx) => (
                              <Card 
                                key={idx} 
                                className={`cursor-pointer transition-all bg-white ${
                                  selectedVariant === idx 
                                    ? 'ring-2 ring-purple-500 shadow-md' 
                                    : 'hover:border-purple-300 hover:shadow-sm'
                                }`}
                                onClick={() => setSelectedVariant(idx)}
                              >
                                <CardContent className="p-3">
                                  {/* Header */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Badge 
                                        variant="outline" 
                                        className={`text-xs font-semibold ${
                                          idx === 0 
                                            ? 'border-blue-300 text-blue-700 bg-blue-50' 
                                            : 'border-amber-300 text-amber-700 bg-amber-50'
                                        }`}
                                      >
                                        Variant {idx === 0 ? 'A' : 'B'}
                                      </Badge>
                                      {selectedVariant === idx && (
                                        <Check className="w-4 h-4 text-purple-600" />
                                      )}
                                    </div>
                                    
                                    {/* QA Status Badge */}
                                    {variant.qaStatus === 'pass' && (
                                      <Badge className="bg-green-100 text-green-700 text-xs border-0">
                                        <Check className="w-3 h-3 mr-1" />
                                        QA Pass
                                      </Badge>
                                    )}
                                    {variant.qaStatus === 'warning' && (
                                      <Badge className="bg-yellow-100 text-yellow-700 text-xs border-0">
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        Warnings
                                      </Badge>
                                    )}
                                    {variant.qaStatus === 'error' && (
                                      <Badge className="bg-red-100 text-red-700 text-xs border-0">
                                        <AlertCircle className="w-3 h-3 mr-1" />
                                        Issues
                                      </Badge>
                                    )}
                                  </div>

                                  {/* Subject if changed */}
                                  {variant.subject && variant.subject !== subject && (
                                    <div className="mb-2 p-2 bg-gray-50 rounded">
                                      <span className="text-xs text-gray-500 block">New Subject:</span>
                                      <p className="text-sm font-medium text-gray-900">{variant.subject}</p>
                                    </div>
                                  )}

                                  {/* Body Preview or Diff */}
                                  <div
                                    className="text-xs text-gray-700 max-h-64 overflow-y-auto p-2 bg-gray-50 rounded"
                                    dir={hasMeaningfulArabicText(`${variant.subject || ''} ${variant.body_html || ''}`) ? 'rtl' : 'ltr'}
                                    style={{
                                      textAlign: hasMeaningfulArabicText(`${variant.subject || ''} ${variant.body_html || ''}`)
                                        ? 'right'
                                        : 'left',
                                    }}
                                  >
                                    {showDiff ? (
                                      renderDiff(body, variant.body_html)
                                    ) : (
                                      <div 
                                        className="prose prose-sm max-w-none [&_p]:mb-3"
                                        dangerouslySetInnerHTML={{ __html: variant.body_html }} 
                                      />
                                    )}
                                  </div>

                                  {/* QA Issues */}
                                  {variant.qaIssues?.length > 0 && (
                                    <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                                      <span className="text-xs font-medium text-yellow-800">Notes:</span>
                                      <ul className="text-xs text-yellow-700 list-disc pl-4 mt-1">
                                        {variant.qaIssues.map((issue, i) => (
                                          <li key={i}>{issue}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Apply Button */}
                                  <Button
                                    size="sm"
                                    className={`w-full mt-3 ${
                                      variant.qaStatus === 'error' 
                                        ? 'bg-gray-400 cursor-not-allowed' 
                                        : 'bg-purple-600 hover:bg-purple-700'
                                    }`}
                                    disabled={variant.qaStatus === 'error'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApplyVariant(variant, idx);
                                    }}
                                  >
                                    {variant.qaStatus === 'error' 
                                      ? 'Cannot apply (QA failed)' 
                                      : `Apply Variant ${idx === 0 ? 'A' : 'B'}`
                                    }
                                  </Button>
                                </CardContent>
                              </Card>
                            ))}
                          </div>

                          {/* Cancel button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAiVariants([]);
                              setSelectedVariant(null);
                            }}
                            className="w-full text-gray-500"
                          >
                            Cancel & Keep Original
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Body */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-[#0A1A2F]">
                    Message
                  </label>
                  {!isOutlookBackfill && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      // Convert HTML to plain text. paragraphBreak: '\n\n' for email, '\n' for LinkedIn
                      const htmlToPlainText = (html, paragraphBreak = '\n\n') => {
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = html;
                        tempDiv.querySelectorAll('p, div').forEach(p => {
                          p.insertAdjacentText('afterend', paragraphBreak);
                        });
                        tempDiv.querySelectorAll('br').forEach(br => {
                          br.replaceWith(document.createTextNode('\n'));
                        });
                        return (tempDiv.textContent || tempDiv.innerText || '')
                          .replace(/\n{3,}/g, paragraphBreak)
                          .trim();
                      };

                      // Helper function to clean HTML for better compatibility
                      const cleanHTML = (html) => {
                        let cleaned = html;
                        cleaned = cleaned.replace(/<p><br><\/p>/gi, '');
                        cleaned = cleaned.replace(/<p>\s*<\/p>/gi, '');
                        cleaned = cleaned.replace(/<br\s*\/?>/gi, '<br>');
                        return cleaned;
                      };

                      try {
                        if (draft.channel === 'email') {
                          // For email: use Clipboard API with Gmail/Outlook-compatible HTML format
                          // Gmail requires <!--StartFragment--> and <!--EndFragment--> to recognize HTML
                          const htmlContent = cleanHTML(body);
                          const plainText = htmlToPlainText(body, '\n\n');
                          const htmlForClipboard = `<!DOCTYPE html><html><body><!--StartFragment-->${htmlContent}<!--EndFragment--></body></html>`;

                          const blob = new Blob([htmlForClipboard], { type: 'text/html' });
                          const plainBlob = new Blob([plainText], { type: 'text/plain' });
                          await navigator.clipboard.write([
                            new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob })
                          ]);

                          notify({
                            title: 'Copied to clipboard!',
                            description: 'Message copied with formatting. Paste into Gmail or Outlook.',
                          });
                          trackMessageExported();
                        } else if (isLinkedInDraft) {
                          // For LinkedIn: single line breaks (Shift+Enter style), compact format
                          const plainText = htmlToPlainText(body, '\n');
                          await navigator.clipboard.writeText(plainText);

                          notify({
                            title: 'Copied to clipboard!',
                            description: 'Message copied (ready for LinkedIn)',
                          });
                          trackMessageExported();
                        } else {
                          // For Call/other: standard plain text with paragraph breaks
                          const plainText = htmlToPlainText(body, '\n\n');
                          await navigator.clipboard.writeText(plainText);

                          notify({
                            title: 'Copied to clipboard!',
                            description: 'Message content copied',
                          });
                          trackMessageExported();
                        }
                      } catch (error) {
                        console.error('Copy error:', error);
                        try {
                          const plainText = htmlToPlainText(body, '\n\n');
                          await navigator.clipboard.writeText(plainText);
                          notify({
                            title: 'Copied to clipboard!',
                            description: 'Message content copied (plain text format)',
                          });
                          trackMessageExported();
                        } catch (fallbackError) {
                          console.error('Fallback copy failed:', fallbackError);
                          notify({
                            title: 'Copy failed',
                            description: 'Please select and copy manually',
                            variant: 'destructive'
                          });
                        }
                      }
                    }}
                    className="text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy Message
                  </Button>
                  )}
                </div>

               {isOutlookBackfill ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex gap-3 mb-12">
                  <Info className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" aria-hidden />
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="font-medium text-gray-700">Original message not stored</p>
                    <p>
                      This send was reconstructed from an Outlook Sent-folder import — subject and
                      timestamp are accurate, but the original body wasn&apos;t preserved in the log.
                    </p>
                  </div>
                </div>
               ) : draft.channel === 'email' ? (
                <div dir={isRtlMessage ? 'rtl' : 'ltr'} className={`email-editor-wrapper ${isRtlMessage ? 'email-editor-rtl' : ''}`}>
                  <ReactQuill
                    value={body}
                    onChange={setBody}
                    readOnly={isSent || isHistoryItem || isAdHocScheduledDraft}
                    modules={{
                      toolbar: isSent || isHistoryItem || isAdHocScheduledDraft ? false : [
                        [{ header: [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['link'],
                        ['clean'],
                      ],
                      clipboard: {
                        matchVisual: false,
                      },
                    }}
                    formats={[
                      'header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link',
                    ]}
                    preserveWhitespace
                    placeholder="Write your email..."
                    className="h-[300px] md:h-[400px] mb-12"
                  />
                  <style jsx>{`
                    .email-editor-wrapper .ql-editor p {
                      margin-bottom: 20px !important;
                    }
                    .email-editor-wrapper .ql-editor p:last-child {
                      margin-bottom: 0 !important;
                    }
                    .email-editor-wrapper.email-editor-rtl .ql-editor {
                      direction: rtl;
                      text-align: right;
                    }
                    .email-editor-wrapper .ql-editor a {
                      color: #00a3a3;
                      text-decoration: underline;
                      text-underline-offset: 2px;
                      cursor: pointer;
                    }
                    .email-editor-wrapper .ql-editor a:hover {
                      color: #008585;
                    }
                  `}</style>
                </div>
                ) : (
                  <textarea
                    value={body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')}
                    onChange={(e) => setBody(e.target.value)}
                    disabled={isSent}
                    dir={isRtlMessage ? 'rtl' : 'ltr'}
                    placeholder={
                      isLinkedInDraft
                        ? (isLinkedInConnectRequest
                            ? 'Write your connection request note...'
                            : 'Write your LinkedIn message...')
                        : "Write your call script..."
                    }
                    className="w-full h-[300px] md:h-[400px] p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#00C2C2] focus:border-transparent"
                    style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', textAlign: isRtlMessage ? 'right' : 'left' }}
                  />
                )}  
              </div>
            </div>
          </div>

          {/* Right Sidebar - Evidence & Variables - Hidden on mobile */}
          <div className="hidden md:block w-80 border-l bg-gray-50 overflow-y-auto">
            <Tabs defaultValue="evidence" className="w-full">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="evidence">Evidence</TabsTrigger>
                <TabsTrigger value="variables">Variables</TabsTrigger>
              </TabsList>

              <TabsContent value="evidence" className="p-4 space-y-4">
                {evidence ? (
                  evidenceHasContent(evidence) ? (
                  <>
                    {evidence.momentum_signals?.length > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-4 h-4 text-green-600" />
                            <h3 className="font-semibold text-sm">Momentum Signals</h3>
                          </div>
                          <div className="space-y-3">
                            {evidence.momentum_signals.map((signal, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="text-[#0A1A2F] font-medium mb-1">{signal.title}</p>
                                <p className="text-[#5C6B7A] text-xs mb-2">{signal.summary}</p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => insertEvidence(signal.summary)}
                                >
                                  <Copy className="w-3 h-3 mr-2" />
                                  Copy to use
                                </Button>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {evidence.persona_activities?.length > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                            <h3 className="font-semibold text-sm">Recent Activity</h3>
                          </div>
                          <div className="space-y-3">
                            {evidence.persona_activities.map((activity, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="text-[#0A1A2F] mb-2">
                                  {activity.description}
                                  {activity.score != null && (
                                    <span className="ml-2 text-xs text-[#5C6B7A] font-medium">
                                      {formatEvidenceScore(activity.score)}
                                    </span>
                                  )}
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => insertEvidence(activity.description)}
                                >
                                  <Copy className="w-3 h-3 mr-2" />
                                  Copy to use
                                </Button>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {evidence.kpis?.length > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Award className="w-4 h-4 text-blue-600" />
                            <h3 className="font-semibold text-sm">KPIs & Outcomes</h3>
                          </div>
                          <div className="space-y-2">
                            {evidence.kpis.map((kpi, idx) => (
                              <Button
                                key={idx}
                                size="sm"
                                variant="outline"
                                className="w-full justify-start text-left"
                                onClick={() => insertEvidence(`${kpi.name}: ${kpi.value}`)}
                              >
                                <Copy className="w-3 h-3 mr-2 flex-shrink-0" />
                                <span className="text-xs">{kpi.name}: {kpi.value}</span>
                              </Button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {evidence.case_studies?.length > 0 && (
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-4 h-4 text-orange-600" />
                            <h3 className="font-semibold text-sm">Case Studies</h3>
                          </div>
                          <div className="space-y-2">
                            {evidence.case_studies.map((cs, idx) => (
                              <Button
                                key={idx}
                                size="sm"
                                variant="outline"
                                className="w-full justify-start text-left"
                                onClick={() => insertEvidence(`${cs.title}: ${cs.url}`)}
                              >
                                <Copy className="w-3 h-3 mr-2 flex-shrink-0" />
                                <span className="text-xs truncate">{cs.title}</span>
                              </Button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                  ) : (
                    <p className="text-sm text-[#5C6B7A] leading-relaxed">
                      No signals used — this message was written from the persona profile
                    </p>
                  )
                ) : (
                  <div className="text-center py-8 text-[#5C6B7A]">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    <p className="text-sm">Loading evidence...</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="variables" className="p-4 space-y-3">
                <p className="text-xs text-[#5C6B7A] mb-4">
                  Click to insert variables that will be auto-filled when sent
                </p>
                
                {[
                  { var: 'contact.firstName', label: 'First Name' },
                  { var: 'contact.lastName', label: 'Last Name' },
                  { var: 'contact.title', label: 'Job Title' },
                  { var: 'account.name', label: 'Account Name' },
                  { var: 'account.account_tier', label: 'Account Tier' },
                  { var: 'user.firstName', label: 'Your First Name' },
                  { var: 'user.lastName', label: 'Your Last Name' },
                  { var: 'user.signature', label: 'Your Signature' },
                ].map(({ var: variable, label }) => (
                  <Button
                    key={variable}
                    size="sm"
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => insertVariable(variable)}
                  >
                    <span className="text-sm">{label}</span>
                    <Badge variant="secondary" className="text-xs font-mono">
                      {`{{${variable}}}`}
                    </Badge>
                  </Button>
                ))}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Schedule Picker Modal */}
      {showSchedulePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Pick Send Time</h3>
            <div className="space-y-4">
              <RecommendedSendTimeCard
                draft={channelDraft}
                tenantId={tenantId}
                userId={scheduleUserId}
                onApply={(iso) => {
                  if (!iso) return;
                  setCustomScheduleTime(isoToDatetimeLocalValue(iso));
                }}
              />
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={customScheduleTime}
                  onChange={(e) => setCustomScheduleTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSchedulePicker(false);
                    setCustomScheduleTime('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!customScheduleTime) return;
                    
                    setShowSchedulePicker(false);
                    setIsSending(true);
                    
                    try {
                      const { provider } = await getConnectedEmailProviderTokens();
                      const scheduledDate = new Date(customScheduleTime);

                      const personaId = draft.persona_id || draft.person_id || draft.personId || draft.person?.persona_id || draft.person?.person_id || draft.person?.id;
                      let currentEmail = fullPerson?.email || personEmail || draft.person?.email;
                      if (personaId && !currentEmail) {
                        try {
                          const personaResponse = await fetch(
                            `${API_BASE_URL}/api/analytics-v2/persona/${personaId}?tenant_id=${tenantId}`,
                            { credentials: 'include' }
                          );
                          if (personaResponse.ok) {
                            const persona = await personaResponse.json();
                            currentEmail = persona.email;
                          }
                        } catch (err) {
                          console.warn('⚠️ Could not fetch email from personas table for schedule modal:', err);
                        }
                      }
                      if (!currentEmail) {
                        throw new Error('Recipient email not found');
                      }

                      const response = await fetch(`${API_BASE_URL}/api/outbox/${draftId}/send`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(buildAdHocSendPayload({
                          provider,
                          personaId,
                          currentEmail,
                          sendMethod: 'scheduled',
                          scheduledAtISO: scheduledDate.toISOString(),
                        }))
                      });

                      if (!response.ok) {
                        throw new Error(await buildProviderAwareError(response, provider));
                      }

                      notify({
                        title: 'Email scheduled!',
                        description: `Will send at ${format(scheduledDate, 'PPP p')}`,
                      });

                      if (onSent) onSent(draftId);
                      onClose();

                    } catch (error) {
                      console.error('Error scheduling:', error);
                      notify({
                        title: 'Failed to schedule',
                        description: error.message,
                        variant: 'destructive'
                      });
                    } finally {
                      setIsSending(false);
                      setCustomScheduleTime('');
                    }
                  }}
                  disabled={!customScheduleTime}
                  className="flex-1 bg-[#00C2C2] hover:bg-[#00A3A3]"
                >
                  Schedule
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}